import React, { useEffect, useRef, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useQueueStore } from "@/stores/queue-store";
import { useAuth } from "@/hooks/use-auth";
import { useGameNotifications } from "@/hooks/use-game-notifications";
import { apiRequest } from '@/lib/queryClient';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';

export function MatchDialog() {
  // Get state from queue store
  const { 
    matchToken, 
    showMatchDialog, 
    matchOpponent, 
    timeRemaining, 
    acceptedMatch, 
    setTimeRemaining, 
    setAcceptedMatch, 
    setShowMatchDialog,
    resetMatchDialog
  } = useQueueStore();
  
  const { user } = useAuth();
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  const { showNotification } = useGameNotifications();
  
  // References for timeout and interval
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Listen for GAME_CREATED events and navigate to the game
  useEffect(() => {
    // Create a function to handle WebSocket messages
    const handleWebSocketGameCreated = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "GAME_CREATED" && data.gameId) {
          console.log("[MatchDialog] Game created event received via WebSocket:", data);
          
          // Join the created game
          setShowMatchDialog(false);
          setAcceptedMatch(false);
          
          // Show notification
          showNotification(
            "Match Accepted!",
            "Both players accepted. Joining game...",
            "info",
            3000
          );
          
          // Navigate to the game room
          setLocation(`/game/${data.gameId}`);
        }
      } catch (error) {
        console.error("[MatchDialog] Error handling WebSocket message:", error);
      }
    };
    
    // Handle the custom game_created event
    const handleCustomGameCreated = (event: CustomEvent) => {
      try {
        if (event.detail && event.detail.gameId) {
          console.log("[MatchDialog] Game created event received via CustomEvent:", event.detail);
          
          // Join the created game
          setShowMatchDialog(false);
          setAcceptedMatch(false);
          
          // Show notification
          showNotification(
            "Match Accepted!",
            "Both players accepted. Joining game...",
            "info",
            3000
          );
          
          // Navigate to the game room
          setLocation(`/game/${event.detail.gameId}`);
        }
      } catch (error) {
        console.error("[MatchDialog] Error handling custom game_created event:", error);
      }
    };
    
    // Handle message events for WebSocket broadcast
    const messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === "websocket_message") {
        handleWebSocketGameCreated(event.data.messageEvent);
      }
    };
    
    // Add event listeners
    window.addEventListener("message", messageHandler);
    window.addEventListener("game_created", handleCustomGameCreated as EventListener);
    
    console.log("[MatchDialog] Event listeners registered for game creation events");
    
    return () => {
      // Clean up event listeners
      window.removeEventListener("message", messageHandler);
      window.removeEventListener("game_created", handleCustomGameCreated as EventListener);
      console.log("[MatchDialog] Event listeners removed for game creation events");
    };
  }, [setLocation, setShowMatchDialog, setAcceptedMatch, showNotification]);
  
  // Set up countdown timer when dialog opens
  useEffect(() => {
    if (showMatchDialog) {
      console.log("[MatchDialog] Dialog opened, starting countdown");
      
      // Clear any existing timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      // Start countdown timer
      countdownIntervalRef.current = setInterval(() => {
        setTimeRemaining((prev: number) => {
          if (prev <= 1) {
            // Clear the interval when we reach 0
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Auto-reject after timeout (30 seconds)
      timeoutRef.current = setTimeout(() => {
        if (showMatchDialog) {
          console.log("[MatchDialog] Match timed out, auto-rejecting");
          handleRejectMatch();
          showNotification(
            "Match Expired",
            "The match request has expired.",
            "error",
            5000
          );
        }
      }, 30000);
      
      // Show notification
      showNotification(
        "Match Found!",
        "Accept or reject the match?",
        "info",
        5000
      );
    }
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [showMatchDialog, setTimeRemaining, showNotification]);
  
  // Handle accepting a match
  const handleAcceptMatch = () => {
    // console.log("[MatchDialog] Accepting match:", matchToken);
    
    if (!window.WebSocket) {
      console.error("[MatchDialog] WebSockets not supported");
      toast({
        title: "Browser Error",
        description: "Your browser doesn't support WebSockets. Please use a modern browser.",
        variant: "destructive",
      });
      return;
    }
    
    // Use the main WebSocket connection if possible via the global service
    try {
      // Import the singleton instance directly
      const WebSocketService = require('@/lib/websocket-service').default;
      const wsService = WebSocketService.getInstance();
      
      // Check connection status using our new method
      if (wsService && wsService.isConnected()) {
        console.log("[MatchDialog] Using existing WebSocket connection to accept match");
        
        // Send match acceptance via main connection
        wsService.send({
          type: "ACCEPT_MATCH",
          token: matchToken,
          userId: user?.id,
        });
        
        // Set accepted state to show waiting UI
        setAcceptedMatch(true);
        
        showNotification(
          "Match Accepted",
          "Waiting for other player...",
          "info",
          3000
        );
        
        // Success! No need to create a new connection
        return;
      } else {
        console.log("[MatchDialog] Main WebSocket not connected, will create temporary connection");
      }
    } catch (error) {
      console.error("[MatchDialog] Error using existing WebSocket:", error);
    }
    
    // Fallback to creating a new connection if the main one isn't available
    console.log("[MatchDialog] Creating new WebSocket connection to accept match");
    
    // Create WebSocket URL with Capacitor-aware logic
    let wsUrl;
    // Check if we're running in a Capacitor native app
    if (Capacitor.isNativePlatform()) {
      // For native apps, use the server URL from environment variables
      const serverUrl = import.meta.env.VITE_SERVER_URL || window.location.origin;
      const parsedUrl = new URL(serverUrl);
      wsUrl = `${parsedUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${parsedUrl.host}/ws`;
      console.log('[MatchDialog] Mobile WebSocket - using absolute URL:', wsUrl);
    } else {
      // For web, use relative path which automatically adapts to the current host
      wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws`;
      console.log('[MatchDialog] Web WebSocket - using relative URL:', wsUrl);
    }
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: "ACCEPT_MATCH",
          token: matchToken,
          userId: user?.id,
        })
      );
      
      // Set accepted state to show waiting UI
      setAcceptedMatch(true);
      
      showNotification(
        "Match Accepted",
        "Waiting for other player...",
        "info",
        3000
      );
      
      // IMPORTANT: Keep this connection open longer to receive the GAME_CREATED message
      // Only close after a much longer delay or on component unmount
      setTimeout(() => {
        console.log("[MatchDialog] Closing temporary WebSocket after delay");
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      }, 30000); // Keep open for 10 seconds instead of 1
      
      // Listen for game created messages on this connection too
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "GAME_CREATED" && data.gameId) {
            console.log("[MatchDialog] Game created event received on temp connection:", data);
            
            // Navigate to the game room
            setLocation(`/game/${data.gameId}`);
            
            // Close the connection after we've received the game created event
            if (ws.readyState === WebSocket.OPEN) {
              ws.close();
            }
          }
        } catch (error) {
          console.error("[MatchDialog] Error handling WebSocket message:", error);
        }
      };
    };
    
    ws.onerror = () => {
      console.error("[MatchDialog] WebSocket connection error");
      toast({
        title: "Connection Error",
        description: "Failed to connect to server. Please try again.",
        variant: "destructive",
      });
    };
  };
  
  // Handle rejecting a match
  const handleRejectMatch = () => {
    // console.log("[MatchDialog] Rejecting match:", matchToken);
    
    if (!window.WebSocket) {
      // Just close the dialog if WebSockets aren't available
      resetMatchDialog();
      return;
    }
    
    // Try to use the main WebSocket connection if available
    try {
      const WebSocketService = require('@/lib/websocket-service').default;
      const wsService = WebSocketService.getInstance();
      
      if (wsService && wsService.isConnected()) {
        console.log("[MatchDialog] Using existing WebSocket connection to reject match");
        
        wsService.send({
          type: "REJECT_MATCH",
          token: matchToken,
          userId: user?.id,
        });
        
        // Reset all match-related states
        resetMatchDialog();
        return;
      } else {
        console.log("[MatchDialog] Main WebSocket not connected, will create temporary connection");
      }
    } catch (error) {
      console.error("[MatchDialog] Error using existing WebSocket:", error);
    }
    
    // Fallback to creating a new WebSocket connection
    console.log("[MatchDialog] Creating new WebSocket connection to reject match");
    
    // Create WebSocket URL with Capacitor-aware logic
    let wsUrl;
    // Check if we're running in a Capacitor native app
    if (Capacitor.isNativePlatform()) {
      // For native apps, use the server URL from environment variables
      const serverUrl = import.meta.env.VITE_SERVER_URL || window.location.origin;
      const parsedUrl = new URL(serverUrl);
      wsUrl = `${parsedUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${parsedUrl.host}/ws`;
      console.log('[MatchDialog] Mobile WebSocket (reject) - using absolute URL:', wsUrl);
    } else {
      // For web, use relative path which automatically adapts to the current host
      wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws`;
      console.log('[MatchDialog] Web WebSocket (reject) - using relative URL:', wsUrl);
    }
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: "REJECT_MATCH",
          token: matchToken,
          userId: user?.id,
        })
      );
      
      // Reset all match-related states
      resetMatchDialog();
      
      // Close this temporary WebSocket after sending
      setTimeout(() => ws.close(), 1000);
    };
    
    ws.onerror = () => {
      // Still reset the dialog on error
      resetMatchDialog();
    };
    
    // Clear any pending timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Also clear countdown interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };
  
  // Don't render anything if the dialog shouldn't be shown
  if (!showMatchDialog) {
    return null;
  }
  
  return (
    <div 
      className="fixed inset-0 backdrop-blur-md bg-black/80 z-[9999] flex items-center justify-center" 
      style={{ animation: "fadeIn 0.3s ease-in-out" }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-sm w-full shadow-2xl border-2 border-primary"
        style={{ animation: "scaleIn 0.3s ease-out" }}
      >
        <h3 className="text-xl font-bold mb-3 text-primary">
          Match Found!
        </h3>
        <p className="mb-1 text-base text-gray-800 dark:text-gray-200">Opponent: <span className="font-semibold text-primary">{matchOpponent}</span></p>
        <p className="mb-4 text-base text-gray-800 dark:text-gray-200">Do you accept this match?</p>
        <div className="text-center mb-4">
          <div className="text-2xl font-bold mb-1 text-primary">
            {timeRemaining}
          </div>
          <div className="w-full bg-gray-300 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${(timeRemaining / 30) * 100}%` }}
            ></div>
          </div>
        </div>
        {acceptedMatch ? (
          <div className="flex items-center justify-center flex-col gap-3 mt-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-base text-gray-800 dark:text-gray-200">Waiting for the other player...</p>
          </div>
        ) : (
          <div className="flex justify-between gap-4 mt-3">
            <Button
              variant="destructive"
              className="w-full text-base py-3 text-white"
              onClick={handleRejectMatch}
            >
              Decline
            </Button>
            <Button 
              className="w-full text-base py-3 bg-primary hover:bg-primary/90 text-white" 
              onClick={handleAcceptMatch}
            >
              Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}